import React, { ReactChild, useState, useEffect } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import styled, { css } from 'styled-components/macro';
import { Carousel as ResponsiveCarousel } from 'react-responsive-carousel';
import { ReactComponent as LeftArrow } from 'icons/leftArrow.svg';
import { ReactComponent as RightArrow } from 'icons/rightArrow.svg';
import colors from 'styles/themes/colors';

interface ICarouselProps {
  children: ReactChild[] | JSX.Element[];
  CustomizedNextButton?: any;
  slidesCount?: number;
  hasOnlyNextButton?: boolean;
  saveModalDisplaying?: any;
  withoutButtons?: boolean;
  customSelecetedItemIndex?: number;
  emulateTouch?: boolean;
  overflowVisible?: boolean;
  alignDots?: string;
  disabledDots?: boolean;
  minHeightSlide?: string;
  animationHandler?: 'slide' | 'fade';
}

const arrowStyles = (): any => css<any>`
  position: absolute;
  z-index: 2;
  top: 0;
  width: 30px;
  height: 30px;
  cursor: pointer;
`;

const indicatorStyles = (): any => css<any>`
  width: 8px;
  height: 8px;
  display: inline-block;
  margin: 0 8px;
  border-radius: 4px;
`;

export const CarouselWrapper = styled.div<any>`
  .carousel.carousel-slider {
    padding-bottom: 35px; // for dots
  }
  .slide {
    ${({ minHeightSlide }): string =>
      minHeightSlide ? `min-height: ${minHeightSlide} !important;` : ''}
  }
  .carousel .control-dots {
    text-align: ${({ alignDots }): string =>
      alignDots ? alignDots : 'center'};
  }
  ${({ overflowVisible }): string =>
    overflowVisible
      ? `
    .carousel.carousel-slider {
      overflow: visible;
    }
    .carousel .slider-wrapper {
      overflow: visible;
    }
    .carousel {
      li.selected {
        z-index: 1 !important;
      }
    }

    .slide {
      visibility: hidden;
    }
    .selected {
      visibility: unset;
    }
    .carousel .control-dots {
      z-index: 0;
    }
  `
      : ''}
`;

const ArrowPrev = styled(LeftArrow)`
  ${arrowStyles}
  left: 0;
`;

const ArrowNext = styled(RightArrow)`
  ${arrowStyles}
  right: 0;
`;

export const Indicator = styled('li')<{ selected?: boolean }>`
  ${indicatorStyles}

  background: ${({ selected }): string =>
    selected ? '#0AD6A7' : colors.CasperGrey};
`;

/**
 * Responsive Carousel
 * @param props
 * @param props.children The child components to wrap around
 * @param props.CustomizedNextButton The JSX element for next button
 * @param props.hasOnlyNextButton Boolean variable, shows if only next button should be displayed
 * @param props.slidesCount The number of slides in the carousel
 */
const Carousel: React.FC<ICarouselProps> = (props) => {
  const {
    children,
    CustomizedNextButton,
    hasOnlyNextButton,
    slidesCount,
    withoutButtons,
    customSelecetedItemIndex,
    emulateTouch = true,
    overflowVisible,
    alignDots = 'center',
    disabledDots,
    minHeightSlide,
    animationHandler = 'slide',
  } = props;

  const [selectedItemIndex, setSelectedItemIndex] = useState(0);

  useEffect(() => {
    if (customSelecetedItemIndex) {
      setSelectedItemIndex(customSelecetedItemIndex);
    }
  }, [customSelecetedItemIndex]);

  const renderArrowPrevButton = (
    onClickHandler: any,
    hasPrev: boolean
  ): any => {
    if (hasOnlyNextButton || withoutButtons) return undefined;
    return hasPrev && <ArrowPrev onClick={onClickHandler} />;
  };

  const renderArrowNextButton = (
    onClickHandler: any,
    hasNext: boolean
  ): any => {
    if (withoutButtons) return undefined;
    if (!CustomizedNextButton)
      return hasNext && <ArrowNext onClick={onClickHandler} />;

    const nextButtonLabel =
      slidesCount && slidesCount - 1 === selectedItemIndex ? 'Done' : 'Next';
    return (
      hasNext && (
        <CustomizedNextButton
          onClick={onClickHandler}
          text={nextButtonLabel}
          className="next-button"
        />
      )
    );
  };

  return (
    <CarouselWrapper
      overflowVisible={overflowVisible}
      alignDots={alignDots}
      minHeightSlide={minHeightSlide}
    >
      <ResponsiveCarousel
        showThumbs={false}
        showStatus={false}
        width="100%"
        emulateTouch={emulateTouch}
        selectedItem={selectedItemIndex}
        renderArrowPrev={renderArrowPrevButton}
        renderArrowNext={renderArrowNextButton}
        animationHandler={animationHandler}
        renderIndicator={(onClickHandler, isSelected, index, label): any => (
          <Indicator
            selected={isSelected}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onClick={disabledDots ? (): void => {} : onClickHandler}
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onKeyDown={disabledDots ? (): void => {} : onClickHandler}
            value={index}
            key={index}
            role="button"
            tabIndex={0}
            title={`${label} ${index + 1}`}
            aria-label={`${label} ${index + 1}`}
            className={isSelected ? 'active' : ''}
          />
        )}
        infiniteLoop={animationHandler === 'fade' ? false : true}
        onChange={(index: number): void => setSelectedItemIndex(index)}
      >
        {children}
      </ResponsiveCarousel>
    </CarouselWrapper>
  );
};

export default Carousel;
